// Proactive follow-up DTOs, exactly as the backend's /sentinel/followups contract sends them.

import type { WaitProgram } from '@/modules/protocols/utils/wait-program';
import type { PendingActionDto, SentinelMessageWireDto } from '../types';

export const FOLLOWUP_VIEWS = ['due', 'waiting', 'held', 'drafts', 'handoff', 'ended'] as const;
export type FollowupView = (typeof FOLLOWUP_VIEWS)[number];
/** `sending` is brief: a Sentinel's Send is in flight. */
export type FollowupState = 'waiting' | 'held' | 'queued' | 'sending' | 'ended';

export interface FollowupRow {
  id: string;
  ticketId: string;
  provider: string;
  conversationId: number | null;
  sentinelTicketId: number | null;
  customer: { name: string | null; email: string | null };
  workflow: { id: string | null; name: string | null; type: string | null; nodeId: string | null; changed: boolean };
  kind: 'reply' | 'event';
  step: { id: string; index: number; total: number };
  state: FollowupState;
  mode: 'shadow' | 'live';
  holdReason: string | null;
  endReason: string | null;
  nextDueAt: string | null;
  startedAt: string;
  updatedAt: string;
  lastEvent: { kind: string; at: string; text: string } | null;
  /** `actions`: what Send also does, the ticket actions Brahma sent with the draft (absent from older backends). */
  /**
   * `blocked`: Brahma's safety check stopped the follow-up, and why. `text` is then what it blocked,
   * or empty, for the Sentinel to fix or write before sending.
   */
  draft: { text: string; receivedAt: string; actions?: PendingActionDto[]; blocked?: { reason: string } | null } | null;
  /** `manual`: a person set it on the ticket, so there is no workflow behind it. */
  origin: 'workflow' | 'manual';
}

/** What a person asks for when they set a follow-up on a ticket. */
export interface TicketFollowupInput {
  minutes: number;
  then: 'nudge' | 'remind';
  /** Required for a nudge: what the nudge should say or ask. */
  instruction?: string;
  note?: string;
}

export interface FollowupPage {
  items: FollowupRow[];
  nextCursor: string | null;
  counts: Record<FollowupView, number>;
}

export interface FollowupTimelineEntry {
  at: string;
  kind: string;
  actor: string;
  reason: string | null;
  text: string;
}

export interface FollowupDetail {
  wait: FollowupRow;
  /** The ticket's thread, oldest first. */
  messages: SentinelMessageWireDto[];
  program: WaitProgram;
  namedValues: Record<string, string>;
  timeline: FollowupTimelineEntry[];
}

export interface FollowupOptOut {
  customerKey: string;
  email: string | null;
  createdAt: string;
  createdBy: string | null;
}

export interface FollowupReport {
  from: string;
  to: string;
  counts: {
    scheduled: number;
    held: number;
    cancelled: number;
    drafted: number;
    sent: number;
    wouldSend: number;
    preventedDuplicates: number;
  };
  endedByReason: Record<string, number>;
  closes: { resolvedByDelivery: number; closedAfterReply: number; closedOnTimeout: number };
  sentinel: { sentAsIs: number; sentEdited: number; discarded: number };
}

export interface FollowupFilters {
  workflowId: string | null;
  reason: string | null;
  search: string;
}

export const EMPTY_FOLLOWUP_FILTERS: FollowupFilters = { workflowId: null, reason: null, search: '' };
